import {
  infiniteQueryOptions,
  queryOptions,
  skipToken,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect } from "react";

import { useSocket } from "@/contexts/SocketContext.tsx";
import * as Sentry from "@sentry/react";

import { errorToast } from "@/components/ui/toast.tsx";
import { api, apiErrorMessage } from "@/lib/http.ts";
import type { ServerToClientThreadEvents } from "@/types/socketIo.ts";
import type { ListTasksQuery, TaskListResp, TaskResp, TaskStatus } from "@/types/tasks.ts";

export const taskQueries = {
  /** Every cached task query. */
  all: () => ["tasks"] as const,
  /** Every account-wide query — one cache entry per filter combination. */
  allAccountTasks: () => [...taskQueries.all(), "account"] as const,
  /**
   * The account's tasks across every thread. The filters are part of the key, so
   * each combination is its own cache entry rather than a refetch that discards
   * the view the user just came from.
   */
  accountTasks: ({ status }: TaskFilters = {}) =>
    infiniteQueryOptions({
      queryKey: [...taskQueries.allAccountTasks(), { status }] as const,
      queryFn: async ({ pageParam, signal }) => {
        const queryParams = new URLSearchParams();
        if (status) {
          queryParams.set("status", status);
        }
        queryParams.set("page", pageParam.toString());

        const response = await api.get<TaskListResp>(`/api/a/tasks?${queryParams.toString()}`, {
          signal,
        });
        return response.data;
      },
      initialPageParam: 0,
      getNextPageParam: (lastPage) => lastPage.nextPage ?? undefined,
    }),
  /** Every per-thread query. */
  allThreadTasks: () => [...taskQueries.all(), "thread"] as const,
  /** One thread's complete task record, disabled with `skipToken` when absent. */
  threadTasks: (threadId: number | undefined) =>
    queryOptions({
      queryKey: [...taskQueries.allThreadTasks(), threadId] as const,
      queryFn: threadId
        ? async ({ signal }) => {
            const response = await api.get<TaskListResp>(`/api/a/threads/${threadId}/tasks`, {
              signal,
            });
            return response.data;
          }
        : skipToken,
    }),
};

/** One task status change, addressed by id and by the thread whose list holds it. */
export type TaskStatusChange = { taskId: number; threadId: number; status: TaskStatus };

/**
 * What the account-wide list is narrowed to. Pagination belongs to the query
 * factory rather than view state; every other endpoint filter comes from the
 * shared server contract so the two sides cannot drift.
 */
export type TaskFilters = Omit<ListTasksQuery, "page">;

/**
 * Keeps every mounted task query current from one app-level socket listener.
 *
 * The listener is global because the event is account-scoped and several task
 * surfaces can observe the same React Query cache entry at once. Invalidating a
 * query with no observers only marks it stale, so this stays cheap when no task
 * UI is mounted. Mount once in `App`'s `GlobalSockets`.
 */
export const useTasksSocket = () => {
  const queryClient = useQueryClient();
  const { socket } = useSocket();

  useEffect(() => {
    if (!socket) return;

    const onTaskMutated = (data: Parameters<ServerToClientThreadEvents["task:mutated"]>[0]) => {
      void queryClient.invalidateQueries({ queryKey: taskQueries.allAccountTasks() });
      void queryClient.invalidateQueries({
        queryKey: taskQueries.threadTasks(data.threadId).queryKey,
      });
    };

    // Socket.IO recovery is bounded, so reconnect against canonical task state.
    void queryClient.invalidateQueries({ queryKey: taskQueries.all() });
    socket.on("task:mutated", onTaskMutated);
    return () => {
      socket.off("task:mutated", onTaskMutated);
    };
  }, [socket, queryClient]);
};

/**
 * The user's own move on a task — complete it, drop it, or put it back. No
 * optimistic write: status decides list membership server-side, so a patched row
 * would sit in a list it no longer belongs to until the refetch landed.
 */
export const useUpdateTaskStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ taskId, status }: TaskStatusChange) => {
      const response = await api.post<TaskResp>(`/api/a/tasks/${taskId}/status`, { status });
      return response.data;
    },
    onError: (error, { taskId, status }) => {
      console.error("Failed to update task status:", error);
      Sentry.captureException(error, {
        tags: { component: "useUpdateTaskStatus", operation: "updateTaskStatus" },
        extra: { taskId, status },
      });
      // The server's reason is worth showing: a 409 says which state the task is
      // actually in, which is the one thing that explains the row snapping back.
      errorToast(apiErrorMessage(error) ?? "Couldn't update that task.");
    },
    // On failure too: a rejection means the two sides disagree about where the
    // task stands, and a refetch settles it.
    onSettled: (_task, _error, { threadId }) => {
      // Every account filter is its own cache entry and the change moves the row
      // between them, so they all go — per-thread, only this task's own.
      void queryClient.invalidateQueries({ queryKey: taskQueries.allAccountTasks() });
      void queryClient.invalidateQueries({ queryKey: taskQueries.threadTasks(threadId).queryKey });
    },
  });
};
